import { AlertTriangle } from "lucide-react";
import { fmtEUR } from "../../../utils/moneyFormat";
import {
  URGENT_TITLE,
  isPaidNotConfirmed,
  statusKeepsUrgent,
  urgentProductLines,
  urgentActivityLines,
} from "./urgentBooking";
import styles from "./UrgentBanner.module.css";

const statusTone = (line) => {
  if (line.reserved) return styles.stConfirmed;
  if (line.cancelled) return styles.stCancelled;
  return styles.stPending;
};

const lineHint = (line) => {
  if (line.reserved) return "Reserved with the supplier: keep it or cancel it";
  if (line.cancelled) return "";
  return "Not reserved with the supplier";
};

const summaryText = (lines) => {
  const open = lines.filter((l) => !l.cancelled);
  const reserved = open.filter((l) => l.reserved);
  if (open.length && reserved.length === open.length) {
    return "Every service is reserved with its supplier, but the booking is not confirmed. Check and confirm it, or cancel and refund.";
  }
  if (reserved.length) {
    return "Part of this booking is reserved with the supplier. The customer has paid: complete the rest by hand, or cancel the reservation and refund.";
  }
  return "No supplier booking was made. The customer has paid: refund or book by hand.";
};

/**
 * UI-29: red URGENT banner at the top of the booking page. Shown only while the booking
 * matches the backend's paid-but-not-confirmed definition (see urgentBooking.js); it has no
 * dismiss and goes away by itself once the booking is Confirmed or Cancelled.
 *
 * @param {object} props.booking     the booking as GET /api/bookings/:id returns it
 * @param {string} [props.liveStatus] the page's current status, so a Confirm on this page
 *                                    hides the banner before the refetch lands
 * @param {Array}  [props.activity]  the booking's activity log (raw rows or the page's audit log)
 */
const UrgentBanner = ({ booking, liveStatus, activity = [] }) => {
  if (!isPaidNotConfirmed(booking)) return null;
  if (liveStatus != null && !statusKeepsUrgent(liveStatus)) return null;

  const lines = urgentProductLines(booking.products);
  const logLines = urgentActivityLines(activity);

  return (
    <section className={styles.banner} role="alert" aria-label={URGENT_TITLE} data-testid="urgent-banner">
      <div className={styles.head}>
        <span className={styles.icon}><AlertTriangle size={18} /></span>
        <div className={styles.headText}>
          <div className={styles.title}>{URGENT_TITLE}</div>
          <div className={styles.sub}>
            {summaryText(lines)}
            <span className={styles.amount}>Payment received: {fmtEUR(booking.grandTotal)}</span>
          </div>
        </div>
      </div>

      <ul className={styles.products}>
        {lines.length === 0 && <li className={styles.productEmpty}>No products on this booking.</li>}
        {lines.map((line) => {
          const meta = [line.supplier, line.what, line.dates].filter(Boolean).join(" · ");
          const hint = lineHint(line);
          return (
            <li key={line.id ?? `${line.type}-${meta}`} className={styles.product} data-testid={`urgent-product-${line.type}`}>
              <span className={styles.pType}>{line.type}</span>
              {meta && <span className={styles.pMeta}>{meta}</span>}
              <span className={`${styles.pStatus} ${statusTone(line)}`}>{line.status}</span>
              {line.reference && <span className={styles.pRef}>Ref {line.reference}</span>}
              {hint && <span className={styles.pHint}>{hint}</span>}
            </li>
          );
        })}
      </ul>

      <div className={styles.log}>
        <div className={styles.logLabel}>Activity log</div>
        {logLines.length === 0 ? (
          <div className={styles.logEmpty}>No alert entries in the activity log yet.</div>
        ) : (
          logLines.map((l) => (
            <div key={l.id} className={styles.logLine}>
              {l.at && <span className={styles.logAt}>{l.at}</span>}
              <span className={styles.logAction}>{l.action}</span>
              {l.text && <span className={styles.logText}>{l.text}</span>}
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default UrgentBanner;
